import { create } from 'zustand';
import { DEFAULT_SORT_DIRECTION, DEFAULT_SORT_KEY, EMPTY_VIDEO_FILTERS } from '@/lib/constants/media';
import type { SearchMode, VideoFilters, VideoSortKey, SortDirection } from '@/lib/types/Media';

interface VideoGridState {
  searchText: string;
  searchMode: SearchMode;
  filters: VideoFilters;
  filtersOpen: boolean;
  sortKey: VideoSortKey;
  sortDirection: SortDirection;
  scrollTop: number;
  awaitingBackNavPaint: boolean;
  orderedIdsSnapshot: number[];
  orderedIdsSignature: string | null;
  orderedIdsQueryKey: string | null;
}

interface VideoGridActions {
  setSearchText: (text: string) => void;
  setSearchMode: (mode: SearchMode) => void;
  setFilters: (patch: Partial<VideoFilters>) => void;
  clearFilters: () => void;
  setFiltersOpen: (open: boolean) => void;
  setSort: (key: VideoSortKey) => void;
  setScrollTop: (top: number) => void;
  setOrderedIdsSnapshot: (signature: string, queryKey: string, ids: number[]) => void;
  markBackNavStart: () => void;
  completeBackNavPaint: () => void;
  reset: () => void;
}

const initialState: VideoGridState = {
  searchText: '',
  searchMode: 'fuzzy',
  filters: EMPTY_VIDEO_FILTERS,
  filtersOpen: false,
  sortKey: DEFAULT_SORT_KEY,
  sortDirection: DEFAULT_SORT_DIRECTION,
  scrollTop: 0,
  awaitingBackNavPaint: false,
  orderedIdsSnapshot: [],
  orderedIdsSignature: null,
  orderedIdsQueryKey: null,
};

export const useVideoGridState = create<VideoGridState & VideoGridActions>()((set) => ({
  ...initialState,

  setSearchText: (text) => set({ searchText: text }),

  setSearchMode: (mode) => set({ searchMode: mode }),

  setFilters: (patch) => set((state) => ({ filters: { ...state.filters, ...patch } })),

  // "Clear all" (D5): resets advanced filters and free-text; sort and search mode stay sticky.
  clearFilters: () => set({ filters: EMPTY_VIDEO_FILTERS, searchText: '' }),

  setFiltersOpen: (open) => set({ filtersOpen: open }),

  setSort: (key) =>
    set((state) => {
      if (state.sortKey === key) {
        return {
          sortDirection: state.sortDirection === 'asc' ? 'desc' : 'asc',
        };
      }
      return {
        sortKey: key,
        sortDirection: 'desc',
      };
    }),

  setScrollTop: (top) =>
    set((state) => {
      if (Math.abs(state.scrollTop - top) < 1) return state;
      return { scrollTop: top };
    }),

  setOrderedIdsSnapshot: (signature, queryKey, ids) =>
    set((state) => {
      if (
        state.orderedIdsSignature === signature &&
        state.orderedIdsQueryKey === queryKey &&
        state.orderedIdsSnapshot === ids
      ) {
        return state;
      }

      return {
        orderedIdsSignature: signature,
        orderedIdsQueryKey: queryKey,
        orderedIdsSnapshot: ids,
      };
    }),

  markBackNavStart: () => set({ awaitingBackNavPaint: true }),

  completeBackNavPaint: () => set({ awaitingBackNavPaint: false }),

  reset: () => set(initialState),
}));
